import * as React from 'react'

import { TStatusColorKey } from '~theme/typings'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { IconMinus, IconPlus } from '~ui/components/icons/specific/IconAmountControl'
import { IconX } from '~ui/components/icons/specific/IconX'
import { GridCentered } from '~ui/components/layout/Box'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { Number } from '~ui/components/typography/Number/Number'
import { Text } from '~ui/components/typography/Text/Text'
import { TSpace } from '~ui/styleGuide/spaces'

import { BUTTON_DEFAULT_RADIUS } from '../../buttons/Button/Button'

type TBgColor = Defined<PropsOf<typeof ButtonIcon>['bgColor']>
type TIconColor = Defined<PropsOf<typeof ButtonIcon>['iconColor']>

type TColorFunction = (options: {
  color: TStatusColorKey
  shouldHighlight: boolean
}) => [buttonBg: TBgColor, iconColor: TIconColor]

type TProps = NoChildren &
  SkeletonProps & {
    /**
     * current amount
     */
    value: number
    /**
     * once input hits this value, the decrease button will be either disabled
     * or turns into a delete button (icon) (if allowDelete prop is set to true)
     * default is `1`
     */
    minValue?: number
    /**
     * once input hits this value, the increase button will become disabled
     */
    maxValue?: number
    /**
     * if true, the decrease button turns into a delete button once the minValue is reached
     */
    allowDelete?: boolean
    /**
     * provide the value that the input was initialized with if you want to highlight inputs that the user changed
     * combine with `highlightChange: true`
     */
    initialValue?: number
    /**
     * set true if you want to highlight inputs that the user changed
     * combine with `initialValue: NOT_HIGHLIGHTED_VALUE`
     */
    highlightChange?: boolean
    /**
     * if true, both increase and decrease buttons are disabled
     */
    isDisabled?: boolean
    /**
     * `basic` uses `inputOnPrimary` bg with colorful icons, `colorful` uses semantic colors (green and red by default) as button bg
     * in colorful variant, highlightChange might not be implemented yet
     */
    variant?: 'basic' | 'colorful'
    /**
     * font size of the number (amount)
     */
    fontSize?: PropsOf<typeof Text>['fontSize']
    /**
     * callback called whenever user changes the input (+1 or -1) but also on DELETE!
     * (when delete is allowed and with value = minValue - 1)
     */
    onChange: (newValue: number) => void
    /**
     * `data-testid` for selecting the increase button during tests
     */
    increaseBtnTestId?: string
    /**
     * `data-testid` for selecting the decrease button during tests
     */
    decreaseBtnTestId?: string
  }

const iconSize = `0.9rem`
const buttonWidth = `2.2rem`
const buttonPadding: TSpace = `smallMedium`
const buttonRadius = BUTTON_DEFAULT_RADIUS

// TODO maybe add onLongClick to a Button & use it here to add multiple items at once?
export const AmountControl: React.FC<TProps> = ({
  value,
  onChange,
  maxValue,
  minValue = 1,
  initialValue,
  variant = 'basic',
  fontSize = `s14`,
  isDisabled = false,
  allowDelete = false,
  highlightChange = false,
  showSkeleton = false,
  increaseBtnTestId,
  decreaseBtnTestId,
}) => {
  const getButtonColors: TColorFunction = React.useCallback(
    ({ color, shouldHighlight }) => {
      const showSemanticBg = variant === `colorful` || shouldHighlight

      return showSemanticBg ? [color, `currentColor`] : [`inputOnPrimary`, `specialColors.${color}`]
    },
    [variant],
  )

  const isOnMinValue = value <= minValue
  const isOnMaxValue = maxValue !== undefined && value >= maxValue
  const showDeleteIcon = allowDelete && isOnMinValue

  const shouldHighlightDecrease =
    highlightChange && //
    initialValue !== undefined &&
    value < initialValue

  const shouldHighlightIncrease =
    highlightChange && //
    initialValue !== undefined &&
    value > initialValue

  const [decreaseBtnBg, decreaseIconColor] = getButtonColors({
    color: `error`,
    shouldHighlight: shouldHighlightDecrease,
  })

  const [increaseBtnBg, increaseIconColor] = getButtonColors({
    color: `success`,
    shouldHighlight: shouldHighlightIncrease,
  })

  const isDecreaseDisabled = isDisabled || (!allowDelete && isOnMinValue)
  const isIncreaseDisabled = isDisabled || isOnMaxValue

  return (
    <GridCentered gridTemplateColumns={`${buttonWidth} 1fr ${buttonWidth}`}>
      <Skeleton showSkeleton={showSkeleton}>
        <ButtonIcon
          width={`100%`}
          height={`100%`}
          iconSize={iconSize}
          bgColor={decreaseBtnBg}
          padding={buttonPadding}
          iconColor={decreaseIconColor}
          onClick={() => onChange(value - 1)}
          icon={showDeleteIcon ? IconX : IconMinus}
          shadow={isDecreaseDisabled ? `none` : `light`}
          state={isDecreaseDisabled ? `disabled` : `enabled`}
          radius={{
            topRight: `none`,
            bottomRight: `none`,
            topLeft: buttonRadius,
            bottomLeft: buttonRadius,
          }}
          dataTestId={decreaseBtnTestId}
        />
      </Skeleton>
      <Skeleton showSkeleton={showSkeleton}>
        <Text fontStyle={`boldSemi`} fontSize={fontSize}>
          <Number>{value}</Number>
        </Text>
      </Skeleton>
      <Skeleton showSkeleton={showSkeleton}>
        <ButtonIcon
          icon={IconPlus}
          iconSize={iconSize}
          bgColor={increaseBtnBg}
          padding={buttonPadding}
          iconColor={increaseIconColor}
          onClick={() => onChange(value + 1)}
          shadow={isIncreaseDisabled ? `none` : `light`}
          state={isIncreaseDisabled ? `disabled` : `enabled`}
          radius={{
            topLeft: `none`,
            bottomLeft: `none`,
            topRight: buttonRadius,
            bottomRight: buttonRadius,
          }}
          dataTestId={increaseBtnTestId}
        />
      </Skeleton>
    </GridCentered>
  )
}
